import { ArrowDownRight, ArrowUpRight, Minus } from '@digital-twins/design-system/icons'

import { CitationSource, SourcePill } from '@/components/citations'
import { cn } from '@/lib/utils'
import { RichInlineText } from './RichInlineText'

import { RichKpi } from '@digital-twins/shared-types/assistant-rich-blocks.generated'

interface KpiRowProps {
  kpis: Array<RichKpi>
  resolveSources: (kpi: RichKpi) => Array<CitationSource>
}

// Direction A (Step 6a, picked 2026-10-22): an editorial hairline band. Columns
// follow the answer column's own width, not the viewport: one tile per column
// on a wide answer, two on a narrow one, one stacked on a phone.
const COLUMNS_WIDE: Record<number, string> = {
  1: '@[34rem]:grid-cols-1',
  2: '@[34rem]:grid-cols-2',
  3: '@[34rem]:grid-cols-3',
  4: '@[34rem]:grid-cols-4',
}

const DELTA_TONE = {
  up: 'text-chart-positive',
  down: 'text-chart-negative',
  flat: 'text-muted-foreground',
} as const

/**
 * Hairlines sit between tiles only, never on the outer left or right edge.
 * Stacked: a rule above every tile after the first. Two columns: a rule on
 * the left of the second column and above the second row. One row: a rule on
 * the left of every tile after the first.
 */
function kpiCellClassName(index: number): string {
  const parts = ['min-w-0 border-border/60 py-4 pr-4']
  if (index > 0) {
    parts.push('border-t')
  }
  if (index === 1) {
    parts.push('@[26rem]:border-t-0')
  }
  if (index % 2 === 1) {
    parts.push('@[26rem]:border-l @[26rem]:pl-4')
  }
  if (index >= 2) {
    parts.push('@[34rem]:border-t-0')
  }
  if (index > 0) {
    parts.push('@[34rem]:border-l @[34rem]:pl-4')
  }
  return parts.join(' ')
}

interface DeltaIconProps {
  direction: 'up' | 'down' | 'flat'
}

function DeltaIcon(props: DeltaIconProps) {
  const { direction } = props

  if (direction === 'up') {
    return <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
  }
  if (direction === 'down') {
    return <ArrowDownRight className="h-3.5 w-3.5" aria-hidden />
  }
  return <Minus className="h-3.5 w-3.5" aria-hidden />
}

export function KpiRow(props: KpiRowProps) {
  const { kpis, resolveSources } = props

  return (
    <div className="@container my-7" data-testid="thread-rich-kpis">
      <div
        className={cn(
          'grid grid-cols-1 border-y border-border @[26rem]:grid-cols-2',
          COLUMNS_WIDE[kpis.length] ?? COLUMNS_WIDE[4],
        )}
      >
        {kpis.map((kpi, index) => {
          const sources = resolveSources(kpi)
          const direction = kpi.direction ?? 'flat'
          const delta = kpi.delta ? (
            <span
              className={cn('inline-flex items-center gap-0.5 text-[13px] font-medium tabular-nums', DELTA_TONE[direction])}
              data-testid={`thread-rich-kpi-${index}-delta`}
            >
              <DeltaIcon direction={direction} />
              <RichInlineText text={kpi.delta} />
            </span>
          ) : null
          const caption = kpi.caption ? <span><RichInlineText text={kpi.caption} /></span> : null
          const meta = delta || caption ? (
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
              {delta}
              {caption}
            </div>
          ) : null
          const pill = sources.length > 0 ? (
            <div className="mt-2">
              <SourcePill sources={sources} testId={`thread-citation-marker-kpi-${index}`} />
            </div>
          ) : null

          return (
            <div key={index} className={kpiCellClassName(index)} data-testid={`thread-rich-kpi-${index}`}>
              <div className="text-[13px] leading-[1.35] text-muted-foreground @[26rem]:min-h-[2.7em]">
                <RichInlineText text={kpi.label} />
              </div>
              <div
                className="mt-2 text-[30px] font-semibold leading-[1.1] tracking-[-0.01em] text-foreground tabular-nums [overflow-wrap:anywhere]"
                data-testid={`thread-rich-kpi-${index}-value`}
              >
                <RichInlineText text={kpi.value} />
              </div>
              {meta}
              {pill}
            </div>
          )
        })}
      </div>
    </div>
  )
}
